/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import { useSelector, useDispatch, useStore } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { createTeamConversation } from '../../../actions/teams';
import UserAvatar from './UserAvatar';
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Check, Loader2, X } from 'lucide-react';
import { CustomButton } from '@/components/ui/custom/CustomButton';
import {
	Command,
	CommandList,
	CommandInput,
	CommandItem,
	CommandGroup,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { useIsMobile } from '@/hooks/use-mobile';
import { BottomSheetTextButton } from '@/components/mobile';
import {
	PHONE_DIALOG_CLASS,
	PHONE_DIALOG_HEADER_CLASS,
	PHONE_DIALOG_TITLE_CLASS,
} from './phoneDialog';

const NewChat = ({
	setCustomerConversationAssignedType,
	showStartNewChatDialog,
	handleShowStartNewChatDialog,
}) => {
	const usersFromDB = useSelector((state) =>
		state.users.filter((u) => u.active)
	);
	const auth = useSelector((state) => state.auth);
	const teams = useSelector((state) => state.teams);
	const dispatch = useDispatch();
	const store = useStore();
	const navigate = useNavigate();
	const isMobile = useIsMobile();

	const [loading, setLoading] = useState(false);
	const [searchText, setSearchText] = useState('');
	const [showGroupChatInput, setShowGroupChatInput] = useState(false);
	const [groupChatTitle, setGroupChatTitle] = useState('');

	const filteredUsers = usersFromDB.filter(
		(user) => user._id !== auth.user._id
	);
	const [users, setUsers] = useState([]);

	const isStartButtonDisabled = users.length === 0;

	const createNewGroupChat = () => {
		setGroupChatTitle(buildDefaultTitle());
		setShowGroupChatInput(true);
	};

	const buildDefaultTitle = () => {
		const MAX_DISPLAY_USERS = 3; // the desired maximum number of users you want to display in the title

		const usersToBeAddedToConversation = [...users, auth.user];

		return users.length <= MAX_DISPLAY_USERS
			? usersToBeAddedToConversation
					.map((user) => user.name)
					.join(', ')
			: `${usersToBeAddedToConversation
					.slice(0, MAX_DISPLAY_USERS)
					.map((user) => user.name)
					.join(', ')} and ${
					usersToBeAddedToConversation.length - MAX_DISPLAY_USERS
			  } others`;
	};

	const createNewConversation = async () => {
		setLoading(true);
		const _newUsers = [...users.map((u) => u._id), auth.user._id];
		// A direct message skips the title step, so its title is built here.
		// Reading it back from state would send the empty initial value.
		const title = users.length < 2 ? buildDefaultTitle() : groupChatTitle;

		const result = await dispatch(
			createTeamConversation({
				title,
				_users: _newUsers,
			})
		);

		if (result?.error) {
			setLoading(false);
			toast.error('Could not start the conversation. Please try again.');
			return;
		}

		if (_newUsers.length > 2) {
			setCustomerConversationAssignedType('GROUPS');
		} else {
			setCustomerConversationAssignedType('DIRECT_MESSAGES');
		}

		setLoading(false);
		handleCloseNewChatDialog(false);

		// createTeamConversation makes the new conversation active. Open it
		// through the URL too, so the address and Back match what is on
		// screen (and phones move from the list to the thread).
		let created = result?.teamConversation;
		if (!created?._id) {
			const { _activeTeamConversation, teamConversations } = store.getState().teams;
			created = teamConversations.find((tc) => tc._id === _activeTeamConversation);
		}
		if (created?._id) {
			const isDirectMessage = Boolean(created.isDirectMessage);
			navigate(
				`/groupchat/${isDirectMessage ? 'dms' : 'groups'}/conversation/groups/${created._id}?_location=${created._business}&isDirectMessage=${isDirectMessage}`
			);
		}
	};

	const handleUserSelect = (user) => {
		setSearchText('');
		if (!users.includes(user)) {
			setUsers([...users, user]);
		} else {
			const updatedUsers = users.filter((u) => u._id !== user._id);
			setUsers(updatedUsers);
		}
	};

	const handleCloseNewChatDialog = (state) => {
		setUsers([]);
		setSearchText('');
		setShowGroupChatInput(false);
		handleShowStartNewChatDialog(state);
	};

	React.useEffect(() => {
		const down = (e) => {
			if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
				e.preventDefault();
				handleShowStartNewChatDialog(true);
			}
		};

		document.addEventListener('keydown', down);
		return () => document.removeEventListener('keydown', down);
	}, []);

	// Phones: a full-screen "New chat" page with Cancel and Start / Next in
	// the navigation bar, the chosen people as chips, then everyone to pick from.
	const renderPhone = () => {
		const primary = showGroupChatInput
			? {
					label: 'Create',
					onClick: createNewConversation,
					disabled: loading || groupChatTitle.trim().length === 0,
				}
			: users.length >= 2
				? {
						label: 'Next',
						onClick: createNewGroupChat,
						disabled: loading || isStartButtonDisabled,
					}
				: {
						label: 'Start',
						onClick: createNewConversation,
						disabled: loading || isStartButtonDisabled,
					};

		return (
			<>
				<DialogHeader className={PHONE_DIALOG_HEADER_CLASS}>
					<BottomSheetTextButton
						className="justify-self-start"
						disabled={loading}
						onClick={() =>
							showGroupChatInput
								? setShowGroupChatInput(false)
								: handleCloseNewChatDialog(false)
						}
					>
						{showGroupChatInput ? 'Back' : 'Cancel'}
					</BottomSheetTextButton>
					<DialogTitle className={PHONE_DIALOG_TITLE_CLASS}>
						{showGroupChatInput ? 'Group name' : 'New chat'}
					</DialogTitle>
					<BottomSheetTextButton
						strong
						className="justify-self-end"
						disabled={primary.disabled}
						onClick={primary.onClick}
					>
						{loading ? (
							<Loader2 className="size-5 animate-spin" aria-label="Starting" />
						) : (
							primary.label
						)}
					</BottomSheetTextButton>
				</DialogHeader>
				<DialogDescription className="sr-only">
					{showGroupChatInput
						? 'Enter a title for your group chat'
						: 'Pick one person for a direct message, or several for a group chat.'}
				</DialogDescription>

				{showGroupChatInput ? (
					<div className="space-y-2 px-4 pt-4">
						<Input
							aria-label="Group name"
							placeholder="Enter group chat title..."
							value={groupChatTitle}
							onChange={(e) => setGroupChatTitle(e.target.value)}
							enterKeyHint="done"
							autoFocus
							className="h-11"
						/>
						<p className="text-sm text-muted-foreground">
							{users.length + 1} members: {buildDefaultTitle()}
						</p>
					</div>
				) : (
					<Command className="flex min-h-0 flex-1 flex-col rounded-none bg-transparent">
						{users.length > 0 && (
							<div
								role="group"
								aria-label="Selected people"
								className="flex shrink-0 gap-2 overflow-x-auto border-b border-border/60 px-4 py-2 scrollbar-none"
							>
								{users.map((user) => (
									<button
										type="button"
										key={user._id}
										onClick={() => handleUserSelect(user)}
										aria-label={`Remove ${user.name}`}
										className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full bg-muted px-3 text-sm active:bg-muted/60"
									>
										{user.name}
										<X className="size-4 text-muted-foreground" aria-hidden="true" />
									</button>
								))}
							</div>
						)}
						<CommandInput
							autoFocus
							value={searchText}
							onValueChange={setSearchText}
							placeholder="Search people"
							enterKeyHint="search"
						/>
						<CommandList className="max-h-none min-h-0 flex-1 overscroll-contain pb-[max(1rem,var(--safe-bottom))] kb-open:pb-4">
							<CommandGroup>
								{filteredUsers.map((user) => {
									const selected = users.includes(user);
									return (
										<CommandItem
											key={user._id}
											value={user.name}
											onSelect={() => handleUserSelect(user)}
											aria-selected={selected}
											className="min-h-12 gap-3 px-2"
										>
											<UserAvatar
												avatarHeight="36px"
												avatarWidth="36px"
												fontWeightThin
												showName={false}
												user={user}
											/>
											<span className="min-w-0 flex-1 truncate text-base">{user.name}</span>
											{selected && (
												<Check className="size-5 shrink-0 text-primary" aria-label="Selected" />
											)}
										</CommandItem>
									);
								})}
							</CommandGroup>
						</CommandList>
					</Command>
				)}
			</>
		);
	};

	return (
		<Dialog
			open={showStartNewChatDialog}
			onOpenChange={handleCloseNewChatDialog}
		>
			<DialogContent mobileVariant="fullscreen" hideClose="mobile" className={PHONE_DIALOG_CLASS}>
				{isMobile ? renderPhone() : !showGroupChatInput ? (
					<DialogHeader>
						<DialogTitle>Start a new conversation</DialogTitle>
						<DialogDescription>
							Start a new group chat by selecting multiple users,
							or initiate a direct message with a single user.
						</DialogDescription>
					</DialogHeader>
				) : (
					<DialogHeader>
						<DialogTitle>Group chat title</DialogTitle>
						<DialogDescription>
							Enter a title for your group chat
						</DialogDescription>
					</DialogHeader>
				)}

				{!isMobile && (
					<>
						<div className="flex flex-col w-full max-h-[70dvh]">
							{loading ? (
								<div className="flex justify-center items-center h-full">
									<Loader2 className="size-6 text-primary animate-spin" />
								</div>
							) : !showGroupChatInput ? (
								<div
									className={cn(
										'flex flex-col transition-all duration-300 ease-in-out space-y-1',
										'max-h-[70dvh] overflow-y-auto'
									)}
								>
									<Command className="border border-accent rounded-md p-2">
										<CommandInput
											autoFocus
											value={searchText}
											onValueChange={setSearchText}
											placeholder="Search for a user to add... (e.g. John Doe)"
										/>
										<CommandList>
											<CommandGroup>
												{filteredUsers.map((user) => (
													<CommandItem
														key={user._id}
														value={user.name}
														onSelect={() =>
															handleUserSelect(user)
														}
													>
														<div className="flex space-x-1.5 items-center">
															<UserAvatar
																avatarHeight="30px"
																avatarWidth="30px"
																fontWeightThin
																showName={false}
																user={user}
															/>
															<p className="text-xs font-semibold">
																{user.name}
															</p>
														</div>
													</CommandItem>
												))}
											</CommandGroup>
										</CommandList>
									</Command>

									{users.length > 0 && (
										<div className="border border-accent rounded-md p-2 ">
											<p className="font-semibold text-sm p-2 border-b border-accent">
												Selected members
											</p>

											<div className="flex gap-1 items-center flex-wrap max-h-[80%] overflow-y-auto pt-2">
												{users.map((user) => (
													<CustomButton
														variant="outline"
														key={user._id}
														onClick={() =>
															handleUserSelect(user)
														}
													>
														{user.name}
														<X className="size-4" />
													</CustomButton>
												))}
											</div>
										</div>
									)}
								</div>
							) : (
								<div className="flex flex-col transition-all duration-300 ease-in-out">
									<Input
										placeholder="Enter group chat title..."
										value={groupChatTitle}
										onChange={(e) =>
											setGroupChatTitle(e.target.value)
										}
										autoFocus
									/>
								</div>
							)}
						</div>
						<DialogFooter>
							<DialogClose asChild>
								<CustomButton
									variant="outline"
									onClick={handleCloseNewChatDialog}
									disabled={loading}
								>
									Close
								</CustomButton>
							</DialogClose>
							{users.length < 2 && !showGroupChatInput && (
								<CustomButton
									variant="default"
									onClick={createNewConversation}
									disabled={loading || isStartButtonDisabled}
								>
									Start
								</CustomButton>
							)}
							{users.length >= 2 && !showGroupChatInput && (
								<CustomButton
									variant="default"
									onClick={createNewGroupChat}
									disabled={loading || isStartButtonDisabled}
								>
									Next
								</CustomButton>
							)}

							{showGroupChatInput && users.length >= 2 && (
								<CustomButton
									variant="default"
									onClick={createNewConversation}
									disabled={loading || groupChatTitle.length === 0}
								>
									Start
								</CustomButton>
							)}
						</DialogFooter>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
};

export default NewChat;
